import { Row, Col, Checkbox, Collapse, Radio, Input, Select } from "antd";
import { SearchOutlined } from "@ant-design/icons";
import axios from "axios";
import React, { Component } from "react";
import { Tree, TreeNode } from "react-organizational-chart";
import {
  TableItem,
  InputNumberItem,
  SelectItem,
  ButtonItem,
} from "../../Components/Items";
import {
  filtrarCatalogoSoloMaster,
  filtrarCatalogoPorBusqueda,
} from "./permisosSoloMaster";

const { Panel } = Collapse;

const nodoStyleGrupo = {
  border: "2px solid #00BFD6",
  borderRadius: 8,
  padding: "8px 14px",
  background: "#E6FBFD",
  display: "inline-block",
  minWidth: 180,
};
const nodoStyleSub = {
  border: "1px solid #00BFD6",
  borderRadius: 6,
  padding: "6px 12px",
  background: "#fff",
  display: "inline-block",
  minWidth: 160,
};
const nodoStylePermiso = {
  border: "1px solid #d9d9d9",
  borderRadius: 6,
  padding: "6px 10px",
  background: "#fff",
  display: "inline-block",
  minWidth: 220,
  textAlign: "left",
};

const getOrden = (obj) => {
  if (!obj) return 0;
  return obj.orden ?? obj.numero ?? 0;
};

const subIdDe = (sub) =>
  (sub?._id || sub?.nombre || "sinSubgrupo").toString();

// Editor de permisos reutilizable (Perfiles y Usuarios). Se encarga de cargar el
// catálogo, la búsqueda, el modo básico/avanzado y los toggles de grupo/subgrupo.
// El padre define, vía props, CÓMO se lee y escribe el valor de cada permiso:
//   - getEstadoPermiso(id) -> { activado, valor }    (lectura del estado efectivo)
//   - onToggleVarios(ids, checked)                   (activar/desactivar 1..N permisos)
//   - onChangeValor(id, valor)                       (cambiar el valor monto/selección)
//   - renderBadge(id)            (opcional) etiqueta a la derecha del checkbox
//   - headerExtra                (opcional) nodo a la derecha de la barra superior
//   - mostrarSeleccionarTodos    (opcional) muestra el botón "Seleccionar todos"
//   - placeholder                (opcional) nodo a mostrar en lugar del editor
//   - onCatalogoCargado(catalogo)(opcional) callback cuando se carga el catálogo
class PermisosEditor extends Component {
  state = {
    todasPermisos: null,
    modo: "basico",
    busqueda: "",
  };

  componentDidMount() {
    this.cargarCatalogo();
  }

  cargarCatalogo = async () => {
    if (this.state.todasPermisos) return;
    await axios
      .post(
        "permisos/selectPermisosPGrupos",
        { usuarioPrincipal: this.props.usuarioPrincipal },
        { headers: this.props.headersToken }
      )
      .then((res) => {
        if (res.data.status === 404) return;
        this.setState({ todasPermisos: res.data });
        if (this.props.onCatalogoCargado) this.props.onCatalogoCargado(res.data);
      });
  };

  getCatalogoFiltrado = () => {
    const catalogo = filtrarCatalogoSoloMaster(
      this.state.todasPermisos,
      this.props.tipo
    );
    return filtrarCatalogoPorBusqueda(catalogo, this.state.busqueda);
  };

  estado = (id) =>
    this.props.getEstadoPermiso(id) || { activado: false, valor: null };

  toggleUno = (id, checked) => this.props.onToggleVarios([id], checked);

  toggleGrupo = (grupo, checked) =>
    this.props.onToggleVarios(
      grupo.permisos.map((p) => p._id.toString()),
      checked
    );

  toggleSubgrupo = (grupo, subInfo, checked) => {
    const subId = subIdDe(subInfo);
    const ids = grupo.permisos
      .filter((p) => subIdDe(p.subgrupo) === subId)
      .map((p) => p._id.toString());
    this.props.onToggleVarios(ids, checked);
  };

  seleccionarTodos = () => {
    const catalogo =
      filtrarCatalogoSoloMaster(this.state.todasPermisos, this.props.tipo) || [];
    const ids = catalogo.flatMap((g) =>
      g.permisos.map((p) => p._id.toString())
    );
    this.props.onToggleVarios(ids, true);
  };

  getSubgruposDeGrupo = (grupo) => {
    const mapa = {};
    grupo.permisos.forEach((perm) => {
      const sub = perm.subgrupo || { _id: "sinSubgrupo", nombre: "Sin subgrupo" };
      const subId = subIdDe(sub);
      if (!mapa[subId]) mapa[subId] = { info: sub, permisos: [] };
      mapa[subId].permisos.push(perm);
    });
    return Object.values(mapa).sort(
      (a, b) => getOrden(a.info) - getOrden(b.info)
    );
  };

  esGrupoSeleccionado = (grupo) =>
    grupo.permisos.length > 0 &&
    grupo.permisos.every((p) => this.estado(p._id.toString()).activado);

  esGrupoIndeterminado = (grupo) => {
    const activos = grupo.permisos.filter(
      (p) => this.estado(p._id.toString()).activado
    ).length;
    return activos > 0 && activos < grupo.permisos.length;
  };

  esSubgrupoSeleccionado = (grupo, subInfo) => {
    const subId = subIdDe(subInfo);
    const lista = grupo.permisos.filter((p) => subIdDe(p.subgrupo) === subId);
    return (
      lista.length > 0 &&
      lista.every((p) => this.estado(p._id.toString()).activado)
    );
  };

  esSubgrupoIndeterminado = (grupo, subInfo) => {
    const subId = subIdDe(subInfo);
    const lista = grupo.permisos.filter((p) => subIdDe(p.subgrupo) === subId);
    const activos = lista.filter(
      (p) => this.estado(p._id.toString()).activado
    ).length;
    return activos > 0 && activos < lista.length;
  };

  renderBadge = (id) =>
    this.props.renderBadge ? this.props.renderBadge(id) : null;

  // Las opciones de un permiso "seleccion" llegan agrupadas por empresa:
  // [{ empresaId, empresa, items: [{_id, nombre}] }]. Las renderizamos como
  // OptGroup (mismo patrón que el selector de Proyectos por empresa).
  renderOpcionesSeleccion = (opciones) => {
    const grupos = Array.isArray(opciones) ? opciones : [];
    return grupos.map((g) => (
      <Select.OptGroup key={g.empresaId} label={g.empresa}>
        {(g.items || []).map((o) => {
          const val = (o._id || o.id || o.value || "").toString();
          return (
            <Select.Option key={val} padre={g.empresa} value={val}>
              {o.nombre || o.label || ""}
            </Select.Option>
          );
        })}
      </Select.OptGroup>
    ));
  };

  renderConfiguracion = (perm) => {
    const id = perm._id.toString();
    const efect = this.estado(id);
    const tipo = perm.tipo || "boolean";
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Checkbox
          checked={efect.activado}
          onChange={(e) => this.toggleUno(id, e.target.checked)}
          onClick={(e) => e.stopPropagation()}
        />
        {this.renderBadge(id)}
        {efect.activado && tipo === "monto" ? (
          <InputNumberItem
            size="small"
            dinero
            style={{ flex: 1, minWidth: 140 }}
            value={typeof efect.valor === "number" ? efect.valor : undefined}
            onChange={(v) => this.props.onChangeValor(id, v ?? null)}
            placeholder="Monto máximo"
          />
        ) : null}
        {efect.activado && tipo === "seleccion" ? (
          <SelectItem
            mode="multiple"
            size="small"
            style={{ flex: 1, minWidth: 160 }}
            value={Array.isArray(efect.valor) ? efect.valor : []}
            onChange={(v) => this.props.onChangeValor(id, v)}
            placeholder="Seleccionar..."
          >
            {this.renderOpcionesSeleccion(perm.opciones)}
          </SelectItem>
        ) : null}
      </div>
    );
  };

  getColumns = () => [
    {
      title: "Subgrupo",
      dataIndex: "subgrupo",
      key: "subgrupo",
      render: (value) => value?.nombre,
    },
    {
      title: "Permiso",
      dataIndex: "nombre",
      key: "nombre",
    },
    {
      title: "Configuración",
      dataIndex: "_id",
      key: "_id",
      width: 320,
      render: (_text, perm) => this.renderConfiguracion(perm),
    },
  ];

  renderBasico = () => {
    const todasPermisos = this.getCatalogoFiltrado();
    if (!todasPermisos) return null;
    if (todasPermisos.length === 0)
      return <p style={{ opacity: 0.7 }}>No se encontraron permisos.</p>;
    const columns = this.getColumns();
    const gruposOrdenados = [...todasPermisos].sort(
      (a, b) => getOrden(a) - getOrden(b)
    );
    return (
      <Collapse accordion>
        {gruposOrdenados.map((grupo) => {
          const subgrupos = this.getSubgruposDeGrupo(grupo);
          return (
            <Panel
              key={grupo.grupoId || grupo._id}
              header={
                <Row justify="space-between" align="middle" style={{ width: "100%" }}>
                  <Col>
                    <strong>{grupo.nombre}</strong>{" "}
                    <span style={{ opacity: 0.7 }}>
                      ({grupo.permisos.length} permisos)
                    </span>
                  </Col>
                  <Col>
                    <Checkbox
                      onClick={(e) => e.stopPropagation()}
                      checked={this.esGrupoSeleccionado(grupo)}
                      indeterminate={this.esGrupoIndeterminado(grupo)}
                      onChange={(e) => this.toggleGrupo(grupo, e.target.checked)}
                    >
                      Seleccionar grupo
                    </Checkbox>
                  </Col>
                </Row>
              }
            >
              <Collapse>
                {subgrupos.map((sub) => {
                  const subKey = sub.info?._id || sub.info?.nombre || "sinSubgrupo";
                  return (
                    <Panel
                      key={subKey}
                      header={
                        <Row justify="space-between" align="middle" style={{ width: "100%" }}>
                          <Col>
                            <span style={{ fontWeight: 600 }}>
                              {sub.info?.nombre || "Sin subgrupo"}
                            </span>
                          </Col>
                          <Col>
                            <Checkbox
                              checked={this.esSubgrupoSeleccionado(grupo, sub.info)}
                              indeterminate={this.esSubgrupoIndeterminado(grupo, sub.info)}
                              onClick={(e) => e.stopPropagation()}
                              onChange={(e) =>
                                this.toggleSubgrupo(grupo, sub.info, e.target.checked)
                              }
                            >
                              Seleccionar subgrupo
                            </Checkbox>
                          </Col>
                        </Row>
                      }
                    >
                      <TableItem
                        pagination={false}
                        size="small"
                        columns={columns}
                        dataSource={sub.permisos}
                      />
                    </Panel>
                  );
                })}
              </Collapse>
            </Panel>
          );
        })}
      </Collapse>
    );
  };

  renderNodoPermiso = (perm) => {
    const id = perm._id.toString();
    const efect = this.estado(id);
    const tipo = perm.tipo || "boolean";
    return (
      <div style={{ ...nodoStylePermiso, opacity: efect.activado ? 1 : 0.65 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
          <Checkbox
            checked={efect.activado}
            onChange={(e) => this.toggleUno(id, e.target.checked)}
          >
            <span style={{ fontWeight: 500 }}>{perm.nombre}</span>
          </Checkbox>
          {this.renderBadge(id)}
        </div>
        {efect.activado && tipo === "monto" ? (
          <div style={{ marginTop: 6 }}>
            <InputNumberItem
              size="small"
              dinero
              style={{ width: "100%" }}
              value={typeof efect.valor === "number" ? efect.valor : undefined}
              onChange={(v) => this.props.onChangeValor(id, v ?? null)}
              placeholder="Monto máximo"
            />
          </div>
        ) : null}
        {efect.activado && tipo === "seleccion" ? (
          <div style={{ marginTop: 6 }}>
            <SelectItem
              mode="multiple"
              size="small"
              style={{ width: "100%" }}
              value={Array.isArray(efect.valor) ? efect.valor : []}
              onChange={(v) => this.props.onChangeValor(id, v)}
              placeholder="Seleccionar..."
            >
              {this.renderOpcionesSeleccion(perm.opciones)}
            </SelectItem>
          </div>
        ) : null}
      </div>
    );
  };

  renderAvanzado = () => {
    const todasPermisos = this.getCatalogoFiltrado();
    if (!todasPermisos) return null;
    if (todasPermisos.length === 0)
      return <p style={{ opacity: 0.7 }}>No se encontraron permisos.</p>;
    const grupos = [...todasPermisos].sort((a, b) => getOrden(a) - getOrden(b));
    return (
      <div style={{ overflowX: "auto", paddingBottom: 16 }}>
        {grupos.map((grupo) => {
          const subgrupos = this.getSubgruposDeGrupo(grupo);
          return (
            <div
              key={grupo.grupoId || grupo._id}
              style={{ marginBottom: 32, minWidth: "fit-content" }}
            >
              <Tree
                lineWidth="2px"
                lineColor="#00BFD6"
                lineBorderRadius="6px"
                label={
                  <div style={nodoStyleGrupo}>
                    <Checkbox
                      checked={this.esGrupoSeleccionado(grupo)}
                      indeterminate={this.esGrupoIndeterminado(grupo)}
                      onChange={(e) => this.toggleGrupo(grupo, e.target.checked)}
                    >
                      <strong>{grupo.nombre}</strong>{" "}
                      <span style={{ opacity: 0.7, fontWeight: 400 }}>
                        ({grupo.permisos.length})
                      </span>
                    </Checkbox>
                  </div>
                }
              >
                {subgrupos.map((sub) => {
                  const subKey = sub.info?._id || sub.info?.nombre || "sinSubgrupo";
                  return (
                    <TreeNode
                      key={subKey}
                      label={
                        <div style={nodoStyleSub}>
                          <Checkbox
                            checked={this.esSubgrupoSeleccionado(grupo, sub.info)}
                            indeterminate={this.esSubgrupoIndeterminado(grupo, sub.info)}
                            onChange={(e) =>
                              this.toggleSubgrupo(grupo, sub.info, e.target.checked)
                            }
                          >
                            <strong>{sub.info?.nombre || "Sin subgrupo"}</strong>
                          </Checkbox>
                        </div>
                      }
                    >
                      {sub.permisos.map((perm) => (
                        <TreeNode
                          key={perm._id}
                          label={this.renderNodoPermiso(perm)}
                        />
                      ))}
                    </TreeNode>
                  );
                })}
              </Tree>
            </div>
          );
        })}
      </div>
    );
  };

  render() {
    if (this.props.placeholder) return this.props.placeholder;
    return (
      <>
        <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
          <Col>
            <Radio.Group
              value={this.state.modo}
              onChange={(e) => this.setState({ modo: e.target.value })}
              buttonStyle="solid"
            >
              <Radio.Button value="basico">Básico</Radio.Button>
              <Radio.Button value="avanzado">Avanzado</Radio.Button>
            </Radio.Group>
          </Col>
          <Col flex="auto" style={{ padding: "0 12px" }}>
            <Input
              allowClear
              prefix={<SearchOutlined />}
              placeholder="Buscar grupo, subgrupo o permiso"
              value={this.state.busqueda}
              onChange={(e) => this.setState({ busqueda: e.target.value })}
            />
          </Col>
          <Col>
            {this.props.headerExtra}
            {this.props.mostrarSeleccionarTodos ? (
              <ButtonItem size="small" blanco onClick={this.seleccionarTodos}>
                Seleccionar todos los permisos
              </ButtonItem>
            ) : null}
          </Col>
        </Row>
        {this.state.modo === "basico" ? this.renderBasico() : this.renderAvanzado()}
      </>
    );
  }
}

export default PermisosEditor;
